import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ErrorMessage } from '../components/ErrorMessage';
import { LoadingPage } from '../components/LoadingPage';
import { getDatabaseContext } from '../services/api';
import {
  DatabaseContext as DatabaseContextData,
  GlobalConfiguration,
  ImportMappings,
  PersonalConfiguration,
} from '../types';
import { DEFAULT_CONFIG } from '../utils/configuration';

const DatabaseContext = React.createContext<
  DatabaseContextProviderData | undefined
>(undefined);

interface DatabaseContextProviderData {
  data: DatabaseContextData;
  updateContext: UpdateContext;
}

type UpdateContext = (
  property: keyof DatabaseContextData
) => Dispatch<
  SetStateAction<ImportMappings | GlobalConfiguration | PersonalConfiguration>
>;

export function DatabaseContextProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [importMappings, setImportMappings] = useState<ImportMappings>({});
  const [globalConfiguration, setGlobalConfiguration] =
    useState<GlobalConfiguration>();
  const [personalConfiguration, setPersonalConfiguration] =
    useState<PersonalConfiguration>();

  const updateContext = useCallback(
    (property: keyof DatabaseContextData) => {
      switch (property) {
        case 'importMappings':
          return setImportMappings;
        case 'globalConfiguration':
          return setGlobalConfiguration;
        case 'personalConfiguration':
          return setPersonalConfiguration;
      }
    },
    [setGlobalConfiguration, setImportMappings, setPersonalConfiguration]
  ) as UpdateContext;

  useEffect(() => {
    void (async () => {
      try {
        const { globalConfiguration, personalConfiguration } =
          await getDatabaseContext();
        setPersonalConfiguration(personalConfiguration);
        setGlobalConfiguration(globalConfiguration || DEFAULT_CONFIG);
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const contextValue = useMemo(
    () => ({
      data: {
        importMappings,
        globalConfiguration,
        personalConfiguration: personalConfiguration!,
      },
      updateContext,
    }),
    [importMappings, globalConfiguration, personalConfiguration, updateContext]
  );

  if (isLoading) {
    return <LoadingPage />;
  } else if (error) {
    return (
      <ErrorMessage
        title="Error retrieving data from database"
        text="An error occurred while attempting to retrieve context data from database."
      />
    );
  } else {
    return (
      <DatabaseContext.Provider value={contextValue}>
        {children}
      </DatabaseContext.Provider>
    );
  }
}

export function useDatabaseContext() {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error(
      'Database context not initialized, probably context provider is missing'
    );
  }
  return context;
}
